import React, { useState } from "react";
import { MdAdd, MdDelete, MdUpload, MdLink, MdCalendarToday, MdPerson, MdTitle, MdDescription, MdArrowBack, MdArrowForward, MdCheck } from "react-icons/md";
import documentService from "service/document.service";

const DocumentContentModal = ({ onClose, articleId, onSuccess }) => {
  const [formData, setFormData] = useState({
    title_doc: "",
    auth_doc: "",
    type_doc: "Annexe", // Valeur par défaut
    source_doc: {
      urls: ["", ""], // Un tableau d'URL pour plus de flexibilité
      date: "",
    },
    path_doc: null,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Types de documents possibles
  const documentTypes = [
    "Annexe",
    "Référence",
    "Illustration",
    "Carte",
    "Tableau",
    "Graphique",
    "Protocole",
    "Autre",
  ];

  // Gérer les changements dans les champs du formulaire
  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Gérer le changement d'URL à un index spécifique
  const handleUrlChange = (index, value) => {
    const newUrls = [...formData.source_doc.urls];
    newUrls[index] = value;
    
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: newUrls
      }
    });
  };

  // Ajouter un nouveau champ URL
  const addUrlField = () => {
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: [...formData.source_doc.urls, ""]
      }
    });
  };

  // Supprimer un champ URL
  const removeUrlField = (index) => {
    // Ne pas supprimer s'il reste seulement un champ
    if (formData.source_doc.urls.length <= 1) return;
    
    const newUrls = [...formData.source_doc.urls];
    newUrls.splice(index, 1);
    
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        urls: newUrls
      }
    });
  };

  // Gérer la date de source
  const handleDateChange = (value) => {
    setFormData({
      ...formData,
      source_doc: {
        ...formData.source_doc,
        date: value
      }
    });
  };

  // Gérer le changement de fichier
  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      path_doc: e.target.files[0] || null,
    });
  };

  // Navigation entre les étapes
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Vérification si l'étape actuelle est valide
  const isStepValid = () => {
    if (currentStep === 1) {
      return !!formData.title_doc; // Titre est requis
    }
    return true; // Autres étapes sont valides par défaut
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      title_doc: "",
      auth_doc: "",
      type_doc: "Annexe",
      source_doc: {
        urls: ["", ""],
        date: "",
      },
      path_doc: null,
    });
    setCurrentStep(1);
    setError(null);
    setSuccess(null);
  };

  // Gérer la fermeture avec confirmation si nécessaire
  const handleCloseWithConfirm = () => {
    if (loading) return; // Ne pas permettre la fermeture pendant le chargement

    // Vérifier si des données ont été saisies
    const hasContent =
      formData.title_doc ||
      formData.auth_doc ||
      formData.path_doc ||
      formData.source_doc.urls.some(url => url) ||
      formData.source_doc.date;

    if (hasContent) {
      if (
        window.confirm(
          "Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter?"
        )
      ) {
        resetForm();
        onClose();
      }
    } else {
      resetForm();
      onClose();
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation finale
    if (!formData.title_doc) {
      setError("Le titre du document est obligatoire");
      setCurrentStep(1); // Retour à l'étape du titre
      return;
    }

    if (!formData.path_doc) {
      setError("Veuillez sélectionner un fichier");
      setCurrentStep(3); // Retour à l'étape du fichier
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Conversion du format des URLs pour l'API
      const apiSourceDoc = {
        ...formData.source_doc,
        // Transformer le tableau d'URLs en objet avec clés url1, url2, url3, etc.
        ...formData.source_doc.urls.reduce((acc, url, index) => {
          if (url) {
            acc[`url${index + 1}`] = url;
          }
          return acc;
        }, {})
      };
      
      // Supprimer le tableau urls qui n'est pas nécessaire pour l'API
      delete apiSourceDoc.urls;

      // Préparation des données pour l'API
      const submitData = new FormData();

      // Ajouter tous les champs au FormData
      submitData.append("title_doc", formData.title_doc);
      submitData.append("auth_doc", formData.auth_doc);
      submitData.append("type_doc", formData.type_doc);
      submitData.append("source_doc", JSON.stringify(apiSourceDoc));
      submitData.append("id_art", articleId.id_art); // ID de l'article

      // Ajouter le fichier
      if (formData.path_doc) {
        submitData.append("path_doc", formData.path_doc);
      }

      // Appel à l'API
      const response = await documentService.createDocumentArticle(submitData);
      console.log("Document ajouté avec succès:", response);

      setSuccess("Le document a été ajouté avec succès!");

      // Attendre un peu avant de fermer la modal
      setTimeout(() => {
        onSuccess && onSuccess(response);
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Erreur lors de l'ajout du document:", err);
      setError("Échec de l'ajout du document. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Affichage des indicateurs d'étape
  const renderStepIndicators = () => {
    return (
      <div className="mb-8 flex items-center justify-center">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div key={i} className="flex items-center">
            <div 
              className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                currentStep > i + 1 
                  ? "border-[#57A863] bg-[#57A863] text-white" 
                  : currentStep === i + 1 
                    ? "border-[#57A863] text-[#57A863]" 
                    : "border-gray-300 text-gray-300"
              }`}
            >
              {currentStep > i + 1 ? <MdCheck className="h-6 w-6" /> : i + 1}
            </div>
            
            {i < totalSteps - 1 && (
              <div className={`h-1 w-16 ${
                currentStep > i + 1 ? "bg-[#57A863]" : "bg-gray-300"
              }`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Affichage du contenu des étapes
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Informations de base
            </h3>
            
            {/* Titre du document */}
            <div className="mb-6">
              <label
                htmlFor="title_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdTitle className="mr-2 h-5 w-5 text-gray-500" />
                Titre du document <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="title_doc"
                value={formData.title_doc}
                onChange={(e) => handleInputChange("title_doc", e.target.value)}
                placeholder="Titre du document"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                required
              />
            </div>

            {/* Auteur du document */}
            <div className="mb-6">
              <label
                htmlFor="auth_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdPerson className="mr-2 h-5 w-5 text-gray-500" />
                Auteur du document
              </label>
              <input
                type="text"
                id="auth_doc"
                value={formData.auth_doc}
                onChange={(e) => handleInputChange("auth_doc", e.target.value)}
                placeholder="Nom de l'auteur"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              />
            </div>

            {/* Type de document */}
            <div>
              <label
                htmlFor="type_doc"
                className="flex items-center text-sm font-medium text-gray-700 mb-1"
              >
                <MdDescription className="mr-2 h-5 w-5 text-gray-500" />
                Type de document
              </label>
              <select
                id="type_doc"
                value={formData.type_doc}
                onChange={(e) => handleInputChange("type_doc", e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              >
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Sources
            </h3>
            
            {/* URLs dynamiques */}
            <div className="mb-6">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
                <MdLink className="mr-2 h-5 w-5 text-gray-500" />
                URLs de référence
              </label>
              
              <div className="space-y-4">
                {formData.source_doc.urls.map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <label htmlFor={`url${index + 1}`} className="block text-xs text-gray-600 mb-1">
                          URL {index + 1}
                        </label>
                        <button
                          type="button"
                          onClick={() => removeUrlField(index)}
                          className="text-gray-400 hover:text-red-500"
                          title="Supprimer cette URL"
                        >
                          <MdDelete className="h-4 w-4" />
                        </button>
                      </div>
                      <input
                        type="text"
                        id={`url${index + 1}`}
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        placeholder="https://example.com"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      />
                    </div>
                  </div>
                ))}
                
                {/* Bouton pour ajouter une URL */}
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={addUrlField}
                    className="flex items-center justify-center rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 transition-colors"
                  >
                    <MdAdd className="mr-2 h-5 w-5" /> Ajouter une URL
                  </button>
                </div>
              </div>
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <MdCalendarToday className="mr-2 h-5 w-5 text-gray-500" />
                Date
              </label>
              <input
                type="date"
                id="date"
                value={formData.source_doc.date}
                onChange={(e) => handleDateChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content animate-fadeIn">
            <h3 className="mb-6 text-center text-lg font-medium text-gray-800">
              Téléchargement du fichier
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center justify-center">
                <MdUpload className="mr-2 h-6 w-6 text-[#57A863]" />
                <span className="text-sm font-medium text-gray-700">Fichier <span className="text-red-500">*</span></span>
              </div>
              
              <div className="mt-4 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600 justify-center">
                    <label
                      htmlFor="path_doc"
                      className="relative cursor-pointer rounded-md bg-white font-medium text-[#57A863] focus-within:outline-none focus-within:ring-2 focus-within:ring-[#57A863] focus-within:ring-offset-2 hover:text-[#4a9154]"
                    >
                      <span>Télécharger un fichier</span>
                      <input 
                        id="path_doc" 
                        name="path_doc" 
                        type="file" 
                        onChange={handleFileChange}
                        className="sr-only" 
                        required 
                      />
                    </label>
                    <p className="pl-1">ou glisser-déposer</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    PDF, PNG, JPG jusqu'à 10MB
                  </p>
                </div>
              </div>
              
              {formData.path_doc && (
                <div className="mt-5 flex items-center rounded-md bg-[#eaf5ec] p-3">
                  <div className="flex-shrink-0 p-1">
                    <svg className="h-5 w-5 text-[#57A863]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2 text-sm font-medium text-[#57A863]">
                    {formData.path_doc.name}
                  </div>
                </div>
              )}
            </div>

            {/* Résumé des informations fournies */}
            <div className="mt-8 rounded-md bg-gray-50 p-4">
              <h4 className="mb-2 font-medium text-gray-700">Résumé</h4>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">Titre:</span>{" "}
                  <span className="font-medium">{formData.title_doc || "(Non renseigné)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">Auteur:</span>{" "}
                  <span>{formData.auth_doc || "(Non renseigné)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">Type:</span>{" "}
                  <span>{formData.type_doc}</span>
                </p>
                <p>
                  <span className="text-gray-500">Date:</span>{" "}
                  <span>{formData.source_doc.date || "(Non renseignée)"}</span>
                </p>
                <p>
                  <span className="text-gray-500">URL:</span>{" "}
                  <span>{formData.source_doc.urls.filter(u => u).join(", ") || "(Non renseignée)"}</span>
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className=" p-6 rounded-xl">
      {/* En-tête avec style amélioré */}
      <div className="mb-6 border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            Ajouter un document
          </h2>
          <button
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
            onClick={handleCloseWithConfirm}
            disabled={loading}
            aria-label="Fermer"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Pour l'article: <span className="font-medium text-gray-700">{articleId.title_art.fr}</span>
        </p>
      </div>

      {/* Messages d'erreur ou de succès */}
      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="flex">
            <svg className="mr-2 h-5 w-5 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">
          <div className="flex">
            <svg className="mr-2 h-5 w-5 flex-shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {success}
          </div>
        </div>
      )}

      {/* Indicateurs d'étape */}
      {renderStepIndicators()}

      {/* Formulaire par étapes */}
      <form onSubmit={e => e.preventDefault()} className="pb-6">
        {/* Contenu dynamique selon l'étape */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>

        {/* Navigation entre les étapes */}
        <div className="mt-8 flex justify-between border-t pt-6">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
                disabled={loading}
              >
                <MdArrowBack className="mr-1 h-5 w-5" />
                Précédent
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCloseWithConfirm}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2"
              disabled={loading}
            >
              Annuler
            </button>
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className={`flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${
                  isStepValid() 
                    ? "bg-[#57A863] hover:bg-[#4a9154]" 
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                disabled={!isStepValid() || loading}
              >
                Suivant
                <MdArrowForward className="ml-1 h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className={`flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 ${
                  loading
                    ? "cursor-not-allowed bg-gray-400"
                    : "bg-[#57A863] hover:bg-[#4a9154]"
                }`}
                disabled={loading || !formData.path_doc}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="mr-2 h-4 w-4 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Ajout en cours...
                  </span>
                ) : (
                  <>
                    Ajouter le document
                    <MdCheck className="ml-1 h-5 w-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default DocumentContentModal;